import { DashboardScreen, type DashboardScreenGame } from '@words-happen/screens';
import { type BottomNavKey, UsersIcon } from '@words-happen/ui-components';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createGamesApi, type GamesApi } from '../../api/games';
import { DEFAULT_PLAYER_NAME } from '../../api/whoami';
import { dashboardGames } from '../../games/dashboard-games';
import type { PageVisibility } from '../../games/page-visibility';
import { useMyGames } from '../../games/use-my-games';
import { gamePath, ROUTES } from '../../routes';
import { useSession } from '../../session';

export interface DashboardRootPageProps {
  /** Replaces words-happen-gql's lobby API. */
  gamesApi?: GamesApi;
  /** Replaces the 30-second poll. */
  pollMs?: number;
  /** Replaces the browser's page visibility. */
  visibility?: PageVisibility;
}

const NO_GAMES: DashboardScreenGame[] = [];

const DashboardView: React.FC<{ games: DashboardScreenGame[] }> = ({ games }) => {
  const { player } = useSession();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BottomNavKey>('home');

  return (
    <DashboardScreen
      who={{ name: player?.name ?? DEFAULT_PLAYER_NAME }}
      profileLink={{ href: ROUTES.profile, onOpen: () => navigate(ROUTES.profile) }}
      games={games}
      modes={[
        {
          id: 'two-player',
          color: 'var(--wh-teal)',
          title: 'Two player',
          subtitle: 'Invite or quick match',
          icon: <UsersIcon />,
          onSelect: () => navigate(ROUTES.games),
        },
      ]}
      challenges={[]}
      nav={{ active: activeTab, onNav: setActiveTab }}
    />
  );
};

/** The signed-in dashboard: lists the player's games, polled as on /games. Until they load, or if loading fails, none. */
const SignedInDashboard: React.FC<DashboardRootPageProps> = ({ gamesApi, pollMs, visibility }) => {
  const navigate = useNavigate();
  const [api] = useState(() => gamesApi ?? createGamesApi());
  const { status, games } = useMyGames(api, { pollMs, visibility });
  const rows = status === 'ready' && games ? dashboardGames(games, (gameId) => navigate(gamePath(gameId))) : NO_GAMES;

  return <DashboardView games={rows} />;
};

/**
 * The dashboard, open to everyone. The header's avatar and greeting link to /profile, until the app navigation lands
 * (WH-146). The Two player mode opens the games list (WH-155). Signed in, "Your games" lists the player's active and
 * finished games (WH-218); a row opens the game, /games/:id (WH-156). A guest's dashboard asks the
 * lobby API nothing.
 */
export const DashboardRootPage: React.FC<DashboardRootPageProps> = (props) => {
  const { signedIn } = useSession();

  return signedIn ? <SignedInDashboard {...props} /> : <DashboardView games={NO_GAMES} />;
};
